import React, { useEffect, useState } from 'react';
import { useRecordingState, setRecordingState } from './store';
import { clockOf, QC_BANNER } from '../../lib/recordings';
import './Recorder.css';

/**
 * "Recording · started by Gordon · 03:12", across the top of the call.
 *
 * The consent the firm has chosen by default is announcement, and an
 * announcement that can be missed is not one. So this is red, it is above
 * everything on the page, it carries a live clock so it visibly IS the
 * recording rather than a notice about one, and it stays for as long as the
 * recording does. In ask mode the same strip, amber, says who is being
 * waited on; a decline turns it into one sentence saying who said no.
 *
 * Nothing here starts or stops anything itself except through the store:
 * Stop is offered to the person who started it and to an administrator,
 * and does what the footer's Stop does.
 */
export default function RecordingBanner() {
  const { active, canStop } = useRecordingState();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!active || active.state !== 'recording') return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);

  if (!active) return null;

  if (active.state === 'declined') {
    return (
      <div className="rb-banner is-declined" role="status">
        <span className="rb-dot is-idle" aria-hidden="true" />
        <span className="rb-banner-text">
          <strong>{active.declined || 'Somebody'} declined</strong> — this call is not being recorded.
          {active.reason ? ` (${active.reason})` : ''}
        </span>
        <button type="button" className="rb-banner-btn"
          onClick={() => setRecordingState((s) => ({ ...s, active: null }))}>
          OK
        </button>
      </div>
    );
  }

  if (active.state === 'asking') {
    return (
      <div className="rb-banner is-asking" role="status" aria-live="polite">
        <span className="rb-dot is-amber" aria-hidden="true" />
        <span className="rb-banner-text">
          <strong>{active.mine ? 'You want' : `${active.byName || 'Somebody'} wants`} to record this call</strong>
          {active.pending.length > 0
            ? <> · waiting for {active.pending.join(', ')}…</>
            : active.asked ? <> · answer below</> : <> · waiting for everybody to accept…</>}
        </span>
      </div>
    );
  }

  const elapsed = Math.max(0, (active.paused && active.pausedAt ? active.pausedAt : now) - (active.startedAt || now));

  // Every chat call recorded (4 Nov): the same red strip, saying why, for
  // everybody - and no Stop for anybody, because nobody can stop it.
  if (active.auto) {
    return (
      <div className={`rb-banner is-recording is-qc${active.paused ? ' is-paused' : ''}`} role="status" aria-live="polite" data-auto="">
        <span className={`rb-dot${active.paused ? ' is-amber' : ''}`} aria-hidden="true" />
        <span className="rb-banner-text">
          <strong>{active.paused ? 'Paused' : 'Recording'}</strong>
          <span className="rb-sep" aria-hidden="true">·</span>
          {QC_BANNER}
          {active.paused ? ' (paused by an administrator)' : ''}
          <span className="rb-sep" aria-hidden="true">·</span>
          <time className="rb-clock">{clockOf(elapsed)}</time>
          {active.part > 1 && <span className="rb-kind-tag">part {active.part}</span>}
        </span>
      </div>
    );
  }

  return (
    <div className={`rb-banner is-recording${active.paused ? ' is-paused' : ''}`} role="status" aria-live="polite">
      <span className={`rb-dot${active.paused ? ' is-amber' : ''}`} aria-hidden="true" />
      <span className="rb-banner-text">
        <strong>{active.paused ? 'Paused' : 'Recording'}</strong>
        <span className="rb-sep" aria-hidden="true">·</span>
        started by {active.mine ? 'you' : (active.byName || 'somebody')}
        <span className="rb-sep" aria-hidden="true">·</span>
        <time className="rb-clock">{clockOf(elapsed)}</time>
        {active.kind === 'video' && <span className="rb-kind-tag">audio + video</span>}
      </span>
      {canStop && (
        <button type="button" className="rb-banner-btn" onClick={() => {
          // The footer's Stop owns the capture and the server call; the banner
          // only presses it, so there is one way to stop and not two.
          document.querySelector('.rb-btn.is-on')?.click();
        }}>
          Stop
        </button>
      )}
    </div>
  );
}
